import { useState } from 'react';

const ContactForm = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate required fields
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    const company = formData.get('company') as string;
    const service = formData.get('service') as string;
    const message = formData.get('message') as string;

    if (!name || !email || !phone || !company || !service || !message) {
      setFormStatus('error');
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFormStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    // Validate message length
    if (message.length > 500) {
      setFormStatus('error');
      setErrorMessage('Message must be 500 characters or less.');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d6k4pnu32h7pkqqsu0n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setErrorMessage('Failed to submit form. Please try again.');
      }
    } catch (error) {
      setFormStatus('error');
      setErrorMessage('An error occurred. Please try again later.');
    }
  };

  return (
    <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg border border-gray-100">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-slate-900 mb-4">Send Us a Message</h2>
        <p className="text-base text-gray-600">
          Fill out the form below and our team will get back to you within 24 hours.
        </p>
      </div>

      <form onSubmit={handleSubmit} data-readdy-form id="contact_form">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                maxLength={100}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                maxLength={100}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
                placeholder="john@company.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                maxLength={50}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-semibold text-slate-900 mb-2">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                required
                maxLength={100}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
                placeholder="Your Company"
              />
            </div>
          </div>

          <div>
            <label htmlFor="service" className="block text-sm font-semibold text-slate-900 mb-2">
              Service Interested In <span className="text-red-500">*</span>
            </label>
            <select
              id="service"
              name="service"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select a service</option>
              <option value="IT Helpdesk & Support">IT Helpdesk &amp; Support</option>
              <option value="Network Setup & Management">Network Setup &amp; Management</option>
              <option value="Cybersecurity Audit & Protection">Cybersecurity Audit &amp; Protection</option>
              <option value="Cloud Hosting & Migration">Cloud Hosting &amp; Migration</option>
              <option value="Software Licensing & Management">Software Licensing &amp; Management</option>
              <option value="IT Training & Onboarding">IT Training &amp; Onboarding</option>
              <option value="Remote Monitoring & Management">Remote Monitoring &amp; Management</option>
            </select>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              maxLength={500}
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm resize-none"
              placeholder="Tell us about your project and requirements..."
            ></textarea>
            <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
          </div>

          {formStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              <i className="ri-check-line mr-2"></i>
              Thank you! Your message has been sent successfully. We'll get back to you soon.
            </div>
          )}

          {formStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              <i className="ri-error-warning-line mr-2"></i>
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={formStatus === 'submitting'}
            className="w-full px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-violet-500/50 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {formStatus === 'submitting' ? (
              <span className="flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Sending...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                Send Message
                <i className="ri-send-plane-fill ml-2"></i>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;